import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import apiService from "../Common/Configurations/APIService";
import config from "../Common/Configurations/APIConfig";
import currencyFlags from "../Data/CurrencyFlags.json";
import DefaultData from "../CompanyData/DefaultData";
import ToolsSection from "../StaticPages/ToolsSection";
import TestimonialsSection from "../StaticPages/TestimonialsSection";
import SuppliersSection from "../StaticPages/SuppliersSection";
import PricingSection from "../StaticPages/PricingSection";

const features = [
    { icon: 'ri-flight-takeoff-line', name: 'Flights' },
    { icon: 'ri-building-line', name: 'Hotels' },
    { icon: 'ri-car-line', name: 'Cars' },
    { icon: 'ri-bus-2-line', name: 'Bus' },
    { icon: 'ri-suitcase-line', name: 'Holidays' },
    { icon: 'ri-visa-line', name: 'Visas' },
    { icon: 'ri-magic-line', name: 'AI Packages' },
    { icon: 'ri-whatsapp-line', name: 'AI Chatbot' },
    { icon: 'ri-user-add-line', name: 'Leads' },
    { icon: 'ri-global-line', name: 'Website' },
    { icon: 'ri-bar-chart-line', name: 'Analytics' },
    { icon: 'ri-calculator-line', name: 'Accounting' }, // Added Smart Accounting
    { icon: 'ri-customer-service-2-line', name: 'Support' },
];

export default function SaasRegister() {
    const navigate = useNavigate();
    const [countries, setCountries] = useState([]);
    const [basicValues, setBasicValues] = useState({
        companyName: "", email: "", phoneNumber: "", country: "IN", currency: "INR", password: "",
    });
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [showPassword, setShowPassword] = useState(false);

    useEffect(() => {
        if (currencyFlags && Array.isArray(currencyFlags)) {
            const sortedCountries = [...currencyFlags].sort((a, b) => a.country.localeCompare(b.country));
            setCountries(sortedCountries);
        }
    }, []);

    const handleBasicChange = (e) => {
        const { name, value } = e.target;
        if (name === "country") {
            const selectedCountry = countries.find((country) => country.countryCode === value);
            setBasicValues({ ...basicValues, country: value, currency: selectedCountry?.code || "" });
        } else {
            setBasicValues({ ...basicValues, [name]: value });
        }
        if (errors[name] === false) { setErrors({ ...errors, [name]: true }); }
    };

    const validateBasicForm = () => {
        let temp = {};
        temp.companyName = basicValues.companyName !== "";
        temp.email = basicValues.email !== "" && /\S+@\S+\.\S+/.test(basicValues.email);
        temp.phoneNumber = basicValues.phoneNumber !== "";
        temp.country = basicValues.country !== "";
        temp.password = basicValues.password !== "" && basicValues.password.length >= 6;
        setErrors(temp);
        return Object.values(temp).every((x) => x === true);
    };

    const togglePasswordVisibility = () => setShowPassword((prev) => !prev);
    const applyErrorClass = (field) => (field in errors && errors[field] === false ? " is-invalid" : "");

    const handleBasicSubmit = async (e) => {
        e.preventDefault();
        if (!validateBasicForm()) return;
        setLoading(true);
        setErrors({});
        try {
            const requestData = {
                CompanyName: basicValues.companyName, Email: basicValues.email, PhoneNumber: basicValues.phoneNumber,
                Country: basicValues.country, Currency: basicValues.currency, Password: basicValues.password,
                CompanyType: DefaultData.COMPANYTYPE, UserType: DefaultData.USERTYPE, PlanType: DefaultData.PLANTYPE,
                ParentCompanyId: DefaultData.DEFAULTCOMPANYID, DomainUrl: window.location.origin,
            };
            const response = await apiService.create(config.REGISTERCOMPANY, requestData);
            if (response && response.success === true) {
                navigate("/registration-pending", {
                    replace: true,
                    state: { userEmail: basicValues.email, message: response.message, companyId: response.companyId },
                });
            }
        } catch (error) {
            const errorData = error.response?.data;
            setErrors({ submit: errorData?.message || "Registration failed. Please try again." });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{ backgroundColor: DefaultData.THEMECOLOR1 }}>
            <main className="container-fluid d-flex align-items-center" style={{ minHeight: '100vh' }}>
                <div className="container">
                    <div className="row align-items-center justify-content-center">
                        <div className="col-lg-7 text-center text-lg-start mb-5 mb-lg-0 d-none d-lg-block">
                            <h1 className="display-6 fw-bolder mb-3" style={{ color: DefaultData.THEMECOLOR3 }}>{DefaultData.LANDINGPAGETITLE}</h1>
                            <p className="fs-5 mb-5" style={{ color: DefaultData.THEMECOLOR3, opacity: 0.9 }}>{DefaultData.LANDINGPAGESUBTITLE}</p>
                            <div className="row row-cols-3 row-cols-md-4 row-cols-lg-5 justify-content-center g-4">
                                {features.map((feature) => (
                                    <div className="col" key={feature.name}>
                                        <div className="text-center">
                                            <div
                                                className="shadow-sm"
                                                style={{
                                                    width: '70px',
                                                    height: '70px',
                                                    borderRadius: '50%',
                                                    backgroundColor: DefaultData.THEMECOLOR3,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    color: DefaultData.THEMECOLOR2, // Changed to ThemeColor1 for contrast inside white circle 
                                                    marginBottom: '0.5rem',
                                                    margin: '0 auto'
                                                }}
                                            >
                                                <i className={`${feature.icon} fs-2`}></i>
                                            </div>
                                            <span className="fw-medium" style={{ color: DefaultData.THEMECOLOR3 }}>{feature.name}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="col-lg-5 col-md-7 d-flex justify-content-center">
                            <div className="card shadow-lg border-0" style={{ borderRadius: '1rem', width: '100%', maxWidth: '500px', marginTop: '10px' }}>
                                <div className="card-body p-lg-5 p-4">
                                    <h4 className="fw-bold text-center">Register As Agent</h4>
                                    <p className="text-muted text-center mb-4">Get started in seconds</p>
                                    <form onSubmit={handleBasicSubmit} autoComplete="off" noValidate>
                                        <div className="mb-3">
                                            <label className="form-label">Company Name*</label>
                                            <input type="text" className={`form-control ${applyErrorClass("companyName")}`} name="companyName" value={basicValues.companyName} onChange={handleBasicChange} placeholder="Enter Company Name" required />
                                            {errors.companyName === false && (<div className="invalid-feedback d-block">Please enter your company name.</div>)}
                                        </div>
                                        <div className="mb-3">
                                            <label className="form-label">Email*</label>
                                            <input type="email" className={`form-control ${applyErrorClass("email")}`} name="email" value={basicValues.email} onChange={handleBasicChange} placeholder="Enter Email" required />
                                            {errors.email === false && (<div className="invalid-feedback d-block">Please enter a valid email address.</div>)}
                                        </div>
                                        <div className="mb-3">
                                            <label className="form-label">Phone Number*</label>
                                            <input type="text" className={`form-control ${applyErrorClass("phoneNumber")}`} name="phoneNumber" value={basicValues.phoneNumber} onChange={handleBasicChange} placeholder="Enter Phone Number" maxLength={15} required />
                                            {errors.phoneNumber === false && (<div className="invalid-feedback d-block">Please enter your phone number.</div>)}
                                        </div>
                                        <div className="mb-3">
                                            <label className="form-label">Country</label>
                                            <select className={`form-select ${applyErrorClass("country")}`} name="country" value={basicValues.country} onChange={handleBasicChange} required>
                                                {countries.map((country) => (
                                                    <option key={country.countryCode} value={country.countryCode}>{country.country}</option>
                                                ))}
                                            </select>
                                            {errors.country === false && (<div className="invalid-feedback d-block">Please select your country.</div>)}
                                        </div>
                                        <div className="mb-3">
                                            <label className="form-label">Password*</label>
                                            <div className="position-relative">
                                                <input type={showPassword ? "text" : "password"} className={`form-control pe-5 ${applyErrorClass("password")}`} name="password" value={basicValues.password} onChange={handleBasicChange} placeholder="Min. 6 characters" required />
                                                <button type="button" className="btn btn-link position-absolute end-0 top-50 translate-middle-y text-decoration-none text-muted" onClick={togglePasswordVisibility} style={{ right: '0.5rem' }}>
                                                    <i className={`ri-eye-${showPassword ? 'line' : 'off-line'} fs-5`}></i>
                                                </button>
                                            </div>
                                            {errors.password === false && (<div className="invalid-feedback d-block">Password must be at least 6 characters.</div>)}
                                        </div>
                                        {errors.submit && <div className="alert alert-danger mt-3">{errors.submit}</div>}
                                        <div className="mt-4">
                                            <button
                                                type="submit"
                                                className="btn w-100"
                                                style={{
                                                    backgroundColor: DefaultData.THEMECOLOR1,
                                                    color: DefaultData.THEMECOLOR3
                                                }}
                                                disabled={loading}
                                            >
                                                {loading ? "Registering..." : "REGISTER FOR 30 DAYS TRIAL FREE"}
                                            </button>
                                        </div>

                                        <div className="mt-3 text-center">
                                            <p className="mb-0 text-muted">Already have an account? <Link to="/" className="fw-semibold text-decoration-underline" style={{ color: DefaultData.THEMECOLOR2 }}>Sign In</Link></p>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
            <ToolsSection />
            <PricingSection
                buttonText="Start Free Trial"
                onPlanClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            />
            <TestimonialsSection />
            <SuppliersSection />
        </div>
    );
}